import type { Edge } from "@xyflow/react";
import type { NodeDefinition } from "@/generated";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import type { NodeV2Data } from "@/types/spotflow-v2";
import type { EditorInput } from "@/components/Form/ComplexMarkdownField/types";
import {
  resolveBindingTarget,
  resolveMentionChip,
  type MentionSource,
  type SecretLookupEntry,
} from "./mentionSource";
import { normalizeVariableRefId } from "./variableRefs";
import {
  getUpstreamNodeDistances,
  handleReachesCurrent,
} from "./variableOptions";
import {
  declaredPathVar,
  ON_ERROR_PATH_KEY,
  pathKeysFor,
  splitPathOutputKey,
} from "./pathValues";

export type VariableRefKind = "valid" | "unreachable" | "not_found";

export interface VariableRefMeta {
  sourceLabel: string;
  sourceImage?: string | null;
  sourceIconName?: string | null;
  sourceColor?: string;
  variableLabel: string;
  dataType: string;
  isList: boolean;
  isSecret?: boolean;
}

export interface VariableRefStatus {
  kind: VariableRefKind;
  meta?: VariableRefMeta;
}

export interface VariableRefContext {
  nodeId: string;
  nodeDataMap: Map<string, NodeV2Data>;
  edges: Edge[];
  nodeRegistry: NodeDefinition[];
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>;
  variableBindings?: Record<string, string | undefined>;
  secretsByQualifiedId?: Map<string, SecretLookupEntry>;
}

interface V2OutputSpec {
  name?: string;
  label?: string;
}

function mentionSourceToMeta(source: MentionSource): VariableRefMeta {
  return {
    sourceLabel: source.sourceName,
    sourceImage: source.sourceImage,
    sourceIconName: source.sourceIconName,
    sourceColor: source.sourceColor,
    variableLabel: source.sourceOutput,
    dataType: source.sourceDataType ?? "str",
    isList: source.sourceIsList,
    isSecret: source.isSecret,
  };
}

export function editorInputToMeta(input: EditorInput): VariableRefMeta {
  return {
    sourceLabel: input.sourceName ?? "Unbound",
    sourceImage: input.sourceImage,
    sourceIconName: input.sourceIconName,
    sourceColor: input.sourceColor,
    variableLabel: input.name,
    dataType: input.dataType,
    isList: input.isList,
    isSecret: input.sourceKind === "secret",
  };
}

function bindingValueForSlot(
  slotId: string,
  bindings: Record<string, string | undefined>
): string | null {
  if (bindings[slotId]) return bindings[slotId] ?? null;
  for (const [key, value] of Object.entries(bindings)) {
    if (value && key.endsWith(`__${slotId}`)) return value;
  }
  return null;
}

function isUpstreamReachable(
  sourceNodeId: string,
  currentNodeId: string,
  edges: Edge[]
): boolean {
  if (sourceNodeId === "__secrets__") {
    return true;
  }
  if (sourceNodeId === currentNodeId) return true;
  const upstream = getUpstreamNodeDistances(currentNodeId, edges);
  return upstream.has(sourceNodeId);
}

function getRegistryOutputs(
  nodeId: string,
  ctx: VariableRefContext
): V2OutputSpec[] {
  const nodeData = ctx.nodeDataMap.get(nodeId);
  if (!nodeData) return [];
  const reg = ctx.nodeRegistry.find((r) => r.type === nodeData.nodeType);
  const override = ctx.dynamicNodeConfigs?.get(nodeId);
  return (override?.outputs ?? reg?.outputs ?? []) as V2OutputSpec[];
}

function outputNameMatches(
  outputName: string,
  outputs: V2OutputSpec[],
  nodeConfig: Record<string, unknown> | undefined,
  isInputNode: boolean
): boolean {
  if (outputs.some((o) => o.name === outputName || o.label === outputName)) {
    return true;
  }

  const outputLabels = (nodeConfig?.__output_labels ?? {}) as Record<
    string,
    string
  >;
  if (outputName in outputLabels) return true;
  if (Object.values(outputLabels).includes(outputName)) return true;

  if (isInputNode) {
    const inputLabel = nodeConfig?.label as string | undefined;
    if (inputLabel === outputName) return true;
  }

  return false;
}

function outputExists(
  sourceNodeId: string,
  outputName: string,
  ctx: VariableRefContext
): boolean {
  if (sourceNodeId === "__secrets__") {
    return ctx.secretsByQualifiedId?.has(outputName) ?? true;
  }

  const nodeData = ctx.nodeDataMap.get(sourceNodeId);
  if (!nodeData) return false;

  const nodeConfig = nodeData.nodeConfig as Record<string, unknown> | undefined;

  // The On-error path's error details (`<node>.on_error`) is a synthetic output
  // present whenever the node's failure action is "skip".
  if (outputName === "on_error" && nodeConfig?.on_error_action === "skip") {
    return true;
  }

  const isInputNode =
    nodeData.nodeType === "InputNode" || nodeData.nodeType === "InputNodeV2";

  if (
    outputNameMatches(
      outputName,
      getRegistryOutputs(sourceNodeId, ctx),
      nodeConfig,
      isInputNode
    )
  ) {
    return true;
  }

  // Declared path variables (`<path>.<name>`): the path must be one of the
  // node's current paths and the row must be declared. An exact output-name
  // match above always wins over this interpretation.
  const split = splitPathOutputKey(outputName);
  if (split) {
    const reg = ctx.nodeRegistry.find((r) => r.type === nodeData.nodeType);
    return (
      pathKeysFor(nodeData.nodeType, reg, nodeConfig).some(
        (p) => p.key === split.pathKey
      ) && !!declaredPathVar(nodeConfig, split.pathKey, split.name)
    );
  }

  return false;
}

/** When a ref targets a path-scoped variable (a declared row or the built-in
 * `on_error` details), the handle its consumers must hang off; null for
 * ordinary outputs. */
function pathHandleFor(
  sourceNodeId: string,
  outputName: string,
  ctx: VariableRefContext
): string | null {
  const nodeData = ctx.nodeDataMap.get(sourceNodeId);
  if (!nodeData) return null;
  const nodeConfig = nodeData.nodeConfig as Record<string, unknown> | undefined;
  if (outputName === "on_error" && nodeConfig?.on_error_action === "skip") {
    return ON_ERROR_PATH_KEY;
  }
  const split = splitPathOutputKey(outputName);
  if (!split) return null;
  const reg = ctx.nodeRegistry.find((r) => r.type === nodeData.nodeType);
  const info = pathKeysFor(nodeData.nodeType, reg, nodeConfig).find(
    (p) => p.key === split.pathKey
  );
  return info && declaredPathVar(nodeConfig, split.pathKey, split.name)
    ? info.handleId
    : null;
}

function resolveRefString(
  ref: string,
  ctx: VariableRefContext
): VariableRefStatus {
  const dotIdx = ref.indexOf(".");
  if (dotIdx <= 0) return { kind: "not_found" };

  const sourceNodeId = ref.slice(0, dotIdx);
  const outputName = ref.slice(dotIdx + 1);
  if (!outputName) return { kind: "not_found" };

  if (sourceNodeId !== "__secrets__") {
    if (!ctx.nodeDataMap.has(sourceNodeId)) {
      return { kind: "not_found" };
    }
    if (!outputExists(sourceNodeId, outputName, ctx)) {
      return { kind: "not_found" };
    }
  } else if (!outputExists(sourceNodeId, outputName, ctx)) {
    return { kind: "not_found" };
  }

  const source = resolveBindingTarget(
    sourceNodeId,
    outputName,
    ctx.nodeDataMap,
    ctx.nodeRegistry,
    ctx.secretsByQualifiedId,
    ctx.dynamicNodeConfigs
  );
  const meta = mentionSourceToMeta(source);

  if (!isUpstreamReachable(sourceNodeId, ctx.nodeId, ctx.edges)) {
    return { kind: "unreachable", meta };
  }

  // Path-scoped variables only carry values downstream of their path's
  // handle. Self-refs are exempt — they only occur inside the node's own
  // path rows, where the ref resolves against the node's own outputs.
  if (sourceNodeId !== ctx.nodeId && sourceNodeId !== "__secrets__") {
    const handleId = pathHandleFor(sourceNodeId, outputName, ctx);
    if (
      handleId !== null &&
      !handleReachesCurrent(
        sourceNodeId,
        handleId,
        ctx.nodeId,
        ctx.edges,
        getUpstreamNodeDistances(ctx.nodeId, ctx.edges)
      )
    ) {
      return { kind: "unreachable", meta };
    }
  }

  return { kind: "valid", meta };
}

export function resolveVariableRefStatus(
  refOrSlotId: string,
  ctx: VariableRefContext
): VariableRefStatus {
  const ref = normalizeVariableRefId(refOrSlotId);
  if (ref.includes(".")) {
    const sourceNodeId = ref.slice(0, ref.indexOf("."));
    if (ctx.nodeDataMap.has(sourceNodeId) || sourceNodeId === "__secrets__") {
      return resolveRefString(ref, ctx);
    }
  }

  const bindings = ctx.variableBindings;
  if (bindings) {
    const boundRef = bindingValueForSlot(ref, bindings);
    if (boundRef) return resolveRefString(boundRef, ctx);
  }

  return { kind: "not_found" };
}

export function resolveChipDisplayStatusWithContext(
  inputId: string,
  inputs: EditorInput[],
  ctx: VariableRefContext | null
): VariableRefStatus {
  const normalizedId = normalizeVariableRefId(inputId);
  const reachableInput = inputs.find((input) => input.id === normalizedId);

  if (reachableInput) {
    if (!ctx || reachableInput.sourceKind === "template") {
      return { kind: "valid", meta: editorInputToMeta(reachableInput) };
    }

    let refString = normalizedId;
    const bindings = ctx.variableBindings;
    if (!normalizedId.includes(".") && bindings) {
      const bound = bindingValueForSlot(normalizedId, bindings);
      if (bound) refString = bound;
    }
    if (refString.includes(".")) {
      const flowStatus = resolveVariableRefStatus(refString, ctx);
      if (flowStatus.kind !== "valid") return flowStatus;
    }
    return { kind: "valid", meta: editorInputToMeta(reachableInput) };
  }

  if (!ctx) return { kind: "not_found" };
  return resolveVariableRefStatus(normalizedId, ctx);
}

export function resolvePreviewVariableRefStatus(
  refId: string,
  ctx: VariableRefContext,
  inputs: EditorInput[]
): VariableRefStatus {
  const normalizedId = normalizeVariableRefId(refId);
  const status = resolveChipDisplayStatusWithContext(normalizedId, inputs, ctx);
  if (status.kind !== "not_found") return status;

  let ref = normalizedId;
  if (!ref.includes(".") && ctx.variableBindings) {
    const bound = bindingValueForSlot(ref, ctx.variableBindings);
    if (bound) ref = bound;
  }

  const source = resolveMentionChip(
    ref,
    (ctx.variableBindings ?? {}) as Record<string, string>,
    ctx.nodeDataMap,
    ctx.nodeRegistry,
    ctx.dynamicNodeConfigs
  );
  if (!source) return status;

  const meta = mentionSourceToMeta(source);
  if (!isUpstreamReachable(source.sourceNodeId, ctx.nodeId, ctx.edges)) {
    return { kind: "unreachable", meta };
  }
  return { kind: "valid", meta };
}
